/** Shut-off notices (access cards, RFID tags, insurance, fuel cards) and the outside contacts they go to. */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { EmptyState, when } from './common.tsx';

export function ShutoffsPage() {
  const [status, setStatus] = useState('open');
  const r = useLoad(() => api.get<any>(`/api/shutoffs?status=${status}`), [status]);
  const a = useAction();
  const confirm = (id: string) => {
    const note = window.prompt('Confirmed by whom / how? (optional)');
    if (note === null) return;
    a.run(async () => (await api.post(`/api/shutoffs/${id}/confirm`, { note: note || undefined }), r.reload()));
  };
  return (
    <div className="ops stack">
      <PageHeader crumb="Agency" title="Shut-offs" sub="When an employee leaves or a car leaves the fleet, the outside contacts are emailed. Confirm each one once it’s done." />
      {r.data?.noContacts && <Notice tone="warn">No outside contacts are set up yet, so nothing can be emailed. Add them under Agency → Alerts → Outside contacts.</Notice>}
      <div className="seg-ctl" role="tablist" aria-label="Which notices">
        {(
          [
            ['open', 'Waiting on confirmation'],
            ['all', 'All'],
          ] as const
        ).map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={status === k} className={status === k ? 'on' : ''} onClick={() => setStatus(k)}>
            {l}
          </button>
        ))}
      </div>
      <ErrorBox error={a.error} />
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.notices.length ? (
        <EmptyState icon="check" title="Nothing waiting" />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Shut off</th>
                <th>For</th>
                <th>Emailed</th>
                <th>Confirmed</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {r.data.notices.map((n: any) => (
                <tr key={n.id}>
                  <td>
                    <strong>{n.label}</strong>
                  </td>
                  <td>
                    {n.subject_name}
                    <div className="small muted">{n.subject_kind === 'employee' ? 'Employee' : 'Vehicle'}</div>
                  </td>
                  <td className="small">
                    {n.sent_to.length ? n.sent_to.join(', ') : <span className="warn-text">No contact set up</span>}
                    <div className="muted">
                      {when(n.sent_at)} · {n.sent_by_name ?? ''}
                    </div>
                  </td>
                  <td className="small">{n.confirmed_at ? `${when(n.confirmed_at)} · ${n.confirmed_by_name}${n.note ? ` — ${n.note}` : ''}` : <Badge tone="warn">Not yet</Badge>}</td>
                  <td>
                    {!n.confirmed_at && (
                      <div className="row gap-sm">
                        <button className="btn sm primary" disabled={a.busy} onClick={() => confirm(n.id)}>
                          Confirm done
                        </button>
                        <button className="btn sm" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/shutoffs/${n.id}/resend`), r.reload()))}>
                          Send again
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** On an employee or vehicle: the notices sent for them and whether they're confirmed. */
export function ShutoffStatus({ kind, id }: { kind: 'employee' | 'vehicle'; id: string }) {
  const r = useLoad(() => api.get<any>('/api/shutoffs?status=all').catch(() => null), [id]);
  const rows = (r.data?.notices ?? []).filter((n: any) => n.subject_kind === kind && n.subject_id === id);
  if (!rows.length) return null;
  const open = rows.filter((n: any) => !n.confirmed_at);
  return (
    <Notice tone={open.length ? 'warn' : 'ok'}>
      <strong>Shut-offs:</strong> {rows.map((n: any) => `${n.label} ${n.confirmed_at ? '✓ confirmed' : `emailed ${when(n.sent_at)} — not confirmed yet`}`).join(' · ')}.{' '}
      {open.length > 0 && <a href="/shutoffs">Confirm them</a>}
    </Notice>
  );
}

/** Agency → Alerts: the people outside BlueHQ who shut things off. */
export function OutsideContactsCard() {
  const r = useLoad(() => api.get<any>('/api/outside-contacts').catch(() => null));
  const a = useAction();
  const [f, setF] = useState({ role: 'school_access', name: '', email: '' });
  if (!r.data) return null;
  return (
    <Card title="Outside contacts">
      <p className="small">
        People outside BlueHQ who are emailed when something has to be shut off: the school district and county when an employee leaves (access cards), the RFID contact when a car leaves the fleet, and the county when a car is sold (insurance and fuel card).
      </p>
      <ErrorBox error={a.error} />
      {r.data.roles.map((role: any) => {
        const list = r.data.contacts.filter((c: any) => c.role === role.code);
        return (
          <div key={role.code} className="oc-role">
            <div className="small strong">{role.label}</div>
            {list.length ? (
              <ul className="plain">
                {list.map((c: any) => (
                  <li key={c.id}>
                    {c.name} · <span className="mono small">{c.email}</span>{' '}
                    {r.data.canEdit && (
                      <button className="btn ghost sm" disabled={a.busy} onClick={() => window.confirm(`Remove ${c.name}?`) && a.run(async () => (await api.post(`/api/outside-contacts/${c.id}/remove`), r.reload()))}>
                        Remove
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="small muted">No one yet.</p>
            )}
          </div>
        );
      })}
      {r.data.canEdit && (
        <form
          className="row gap-sm wrap"
          onSubmit={(e) => {
            e.preventDefault();
            a.run(async () => (await api.post('/api/outside-contacts', f), setF({ ...f, name: '', email: '' }), r.reload()));
          }}
        >
          <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} aria-label="Shuts off">
            {r.data.roles.map((x: any) => (
              <option key={x.code} value={x.code}>
                {x.label}
              </option>
            ))}
          </select>
          <input required placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} aria-label="Contact name" />
          <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} aria-label="Contact email" />
          <button className="btn primary" disabled={a.busy}>
            Add contact
          </button>
        </form>
      )}
    </Card>
  );
}
